/**
 * Opens the Coverage calculator section in a popup window, and lets a URL open it.
 *
 * Deliberately does not touch sections/slm-coverage-calculator.liquid — that file is regenerated
 * and re-uploaded regularly, so any edit there would be lost. This works purely from the outside:
 * it finds the rendered section, hides it, and reveals it as an overlay when a trigger is clicked.
 *
 * If no trigger button is on the page the section is left completely alone and renders inline,
 * which is how every other template still behaves.
 *
 * DEEP LINKS
 *   #slmCalc  or  #calculator          opens the popup (scrolls to it on templates with no trigger)
 *   ?calc=1                            same, without a hash
 *   ?project=roof&surface=metal&area=1500
 *                                      opens it with the answer already worked out
 *   &corrugated=1                      adds the 25% uplift (metal surfaces only)
 *   &color=Grey                        sets the membrane color, which also pairs the pigment
 *
 * The prefill drives the calculator's own inputs and fires its own events, so it stays correct
 * even when the section file changes — nothing here depends on the section's internal code.
 */
(function () {
  var host = null;
  var lastFocus = null;
  var hasTriggers = false;

  function calculator() {
    return document.getElementById('slmCalc');
  }

  function open() {
    if (!host) return;
    lastFocus = document.activeElement;
    host.classList.add('is-open');
    host.removeAttribute('aria-hidden');
    // Stop the page behind the overlay from scrolling with it.
    document.documentElement.style.overflow = 'hidden';
    var close = host.querySelector('.slm-modal__close');
    if (close) close.focus();
  }

  function close() {
    if (!host) return;
    host.classList.remove('is-open');
    host.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    // Drop the hash so a refresh or a back-button return doesn't reopen it.
    if (/^#(slmCalc|calculator)$/i.test(location.hash) && history.replaceState) {
      history.replaceState(null, '', location.pathname + location.search);
    }
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  }

  /**
   * Fills the calculator in from URL parameters by driving its own controls.
   * Every value is checked against what the calculator actually offers, so an
   * out-of-date link degrades to an empty calculator rather than a broken one.
   */
  function prefill(q) {
    var calc = calculator();
    if (!calc) return;

    var elProj = document.getElementById('slmProject');
    var elSub = document.getElementById('slmSub');
    var elArea = document.getElementById('slmArea');
    var elGo = document.getElementById('slmGo');
    var elCorr = document.getElementById('slmCorr');
    var elCorrRow = document.getElementById('slmCorrRow');
    if (!elProj || !elArea || !elGo) return;

    function fire(el) {
      var ev;
      try { ev = new Event('change', { bubbles: true }); }
      catch (e) { ev = document.createEvent('HTMLEvents'); ev.initEvent('change', true, false); }
      el.dispatchEvent(ev);
    }
    function pick(sel, want) {
      if (!sel || !want) return false;
      want = String(want).toLowerCase();
      for (var i = 0; i < sel.options.length; i++) {
        if (String(sel.options[i].value).toLowerCase() === want) {
          sel.selectedIndex = i;
          return true;
        }
      }
      return false;
    }
    /**
     * Colors are full product names ("Rockslide Grey", "SEMCO Orange"), which are
     * unpleasant in a URL. Accept a slug, then a unique partial — but never a partial
     * that matches two colors, or "grey" would silently pick one of the two greys.
     */
    function pickColor(sel, want) {
      if (!sel || !want) return false;
      var norm = function (s) { return String(s).toLowerCase().replace(/[^a-z0-9]/g, ''); };
      var w = norm(want), i, hits = [];
      for (i = 0; i < sel.options.length; i++) {
        if (norm(sel.options[i].value) === w) { sel.selectedIndex = i; return true; }
      }
      for (i = 0; i < sel.options.length; i++) {
        if (norm(sel.options[i].value).indexOf(w) !== -1) hits.push(i);
      }
      if (hits.length === 1) { sel.selectedIndex = hits[0]; return true; }
      return false;
    }

    var project = q.get('project');
    if (pick(elProj, project)) {
      fire(elProj);
      // The project's change handler rebuilds the surface dropdown, so this must come after it.
      var surface = q.get('surface') || q.get('sub');
      if (surface && elSub && pick(elSub, surface)) fire(elSub);
    }

    // Only meaningful on metal — the calculator hides the row for anything else.
    if (q.get('corrugated') === '1' && elCorr && elCorrRow && !elCorrRow.hasAttribute('hidden')) {
      elCorr.checked = true;
      fire(elCorr);
    }

    var area = parseFloat(q.get('area'));
    if (area > 0) {
      elArea.value = area;
      elGo.click();

      // The color control only exists once a result has been drawn.
      var color = q.get('color');
      if (color) {
        var csel = document.querySelector('#slmLines select[data-color]');
        if (csel && pickColor(csel, color)) fire(csel);
      }
    } else if (!hasTriggers) {
      // Inline templates: put the cursor where the visitor has to type.
      try { elArea.focus({ preventScroll: true }); } catch (e) { elArea.focus(); }
    }
  }

  /** Decides whether this URL is asking for the calculator, and acts on it. */
  function openFromUrl() {
    var q;
    try { q = new URLSearchParams(location.search); } catch (e) { return; }

    var asked = /^#(slmCalc|calculator)$/i.test(location.hash) ||
                q.has('calc') || q.has('calculator') ||
                q.has('project') || q.has('area');
    if (!asked) return;

    prefill(q);

    if (hasTriggers) {
      open();
    } else {
      // No trigger on this template, so the section is inline — scroll to it instead.
      // Waiting a beat lets lazy images above finish reserving their space.
      var calc = calculator();
      if (!calc) return;
      setTimeout(function () {
        calc.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 320);
    }
  }

  function setup() {
    var calc = calculator();
    if (!calc) return;

    var triggers = document.querySelectorAll('[data-slm-modal-open]');
    hasTriggers = !!triggers.length;

    if (hasTriggers) {
      host = calc.closest('.shopify-section') || calc.parentElement;
      if (!host) return;

      host.classList.add('slm-modal-host');
      host.setAttribute('aria-hidden', 'true');

      if (!calc.querySelector('.slm-modal__close')) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'slm-modal__close';
        btn.setAttribute('aria-label', 'Close');
        btn.innerHTML = '&times;';
        btn.addEventListener('click', close);
        calc.insertBefore(btn, calc.firstChild);
      }

      Array.prototype.forEach.call(triggers, function (t) {
        t.addEventListener('click', function (e) {
          e.preventDefault();
          open();
        });
      });

      // Click the backdrop (the host itself, not the calculator) to dismiss.
      host.addEventListener('click', function (e) {
        if (e.target === host) close();
      });

      document.addEventListener('keydown', function (e) {
        if ((e.key === 'Escape' || e.key === 'Esc') && host.classList.contains('is-open')) close();
      });
    }

    // A link to #slmCalc clicked while already on the page changes the hash without reloading.
    window.addEventListener('hashchange', function () {
      if (/^#(slmCalc|calculator)$/i.test(location.hash)) openFromUrl();
    });

    openFromUrl();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }

  // The theme editor re-renders a section on every setting change, which drops the class and the
  // close button. Re-apply so the popup keeps working while the merchant edits.
  document.addEventListener('shopify:section:load', function () {
    host = null;
    setup();
  });

  // Handy for a custom button anywhere on the site: onclick="SLMCalculator.open()"
  window.SLMCalculator = {
    open: function () { if (hasTriggers) open(); },
    close: close,
    prefill: function (params) {
      try { prefill(new URLSearchParams(params || '')); } catch (e) {}
    }
  };
})();
